import React, { useState, useRef, useEffect } from 'react';
import SendIcon from '@mui/icons-material/Send';
import CircularProgress from '@mui/icons-material/RotateRight';
import AssistantIcon from '@mui/icons-material/Assistant';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import { streamAIReportingAPI } from '../requests/utilities/streamingRequests';
import { API_ai_reporting_chat_stream } from '../../../constants';
import ReactMarkdown from 'react-markdown';
import WidgetRenderer from './charts/WidgetRenderer';

export default function AIReportingDisplay(props) {
  const [messages, setMessages] = useState([
    {
      id: 1,
      content:
        "Hi! I'm your AI reporting assistant. Ask me questions about your store or employee metrics and I'll generate charts and analysis for you.\n\nHere are some things you can ask:\n- \"Show me actual sales for last week\"\n- \"Compare actual SPLH across all stores for the past month\"\n- \"What was our attendance rate this month?\"\n- \"Show scheduled vs actual labor hours this week as a bar chart\"",
      sender: 'ai',
      timestamp: new Date(),
      chartData: null,
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [thinkingText, setThinkingText] = useState('');

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const formatTimestamp = (timestamp) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(timestamp);
  };

  // Build conversation history for context (last 10 messages / 5 exchanges)
  const getConversationHistory = () => {
    const history = messages
      .filter((m) => m.sender === 'user' || m.sender === 'ai')
      .slice(1) // skip greeting
      .slice(-10)
      .map((m) => ({
        role: m.sender === 'user' ? 'user' : 'assistant',
        content: m.content,
      }));
    return history.length > 0 ? history : undefined;
  };

  const handleSendMessage = async () => {
    const text = inputValue.trim();
    if (!text || isTyping) return;

    const userMessage = {
      id: Date.now(),
      content: text,
      sender: 'user',
      timestamp: new Date(),
      chartData: null,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    setThinkingText('Analyzing your question...');

    const aiMessageId = Date.now() + 1;
    let receivedTokens = false;
    let chartData = null;

    const conversationHistory = getConversationHistory();

    try {
      await streamAIReportingAPI(
        API_ai_reporting_chat_stream,
        {
          store_id: props.homeStore,
          user_query: text,
          ...(conversationHistory && { conversation_history: conversationHistory }),
        },
        {
          onThinking: (thinkText) => {
            setThinkingText(thinkText);
          },
          onChartData: (data) => {
            chartData = data;
          },
          onToken: (tokenText) => {
            if (!receivedTokens) {
              receivedTokens = true;
              setThinkingText('');
              setMessages((prev) => [
                ...prev,
                {
                  id: aiMessageId,
                  content: tokenText,
                  sender: 'ai',
                  timestamp: new Date(),
                  chartData: null,
                },
              ]);
            } else {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === aiMessageId
                    ? { ...msg, content: msg.content + tokenText }
                    : msg
                )
              );
            }
          },
          onDone: (donePayload) => {
            const finalChartData = donePayload.chart_data || chartData;
            if (!receivedTokens) {
              setMessages((prev) => [
                ...prev,
                {
                  id: aiMessageId,
                  content: donePayload.text || "I couldn't generate a response. Please try again.",
                  sender: 'ai',
                  timestamp: new Date(),
                  chartData: finalChartData,
                },
              ]);
            } else {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === aiMessageId
                    ? {
                        ...msg,
                        content: donePayload.text || msg.content,
                        chartData: finalChartData || msg.chartData,
                      }
                    : msg
                )
              );
            }
            setIsTyping(false);
            setThinkingText('');
          },
          onError: (errorMsg) => {
            setMessages((prev) => [
              ...prev,
              {
                id: aiMessageId,
                content: `Sorry, an error occurred: ${errorMsg}`,
                sender: 'ai',
                timestamp: new Date(),
                chartData: null,
              },
            ]);
            setIsTyping(false);
            setThinkingText('');
          },
        }
      );
    } catch (error) {
      if (!receivedTokens) {
        setMessages((prev) => [
          ...prev,
          {
            id: aiMessageId,
            content: "Sorry, I couldn't process your request at the moment. Please try again later.",
            sender: 'ai',
            timestamp: new Date(),
            chartData: null,
          },
        ]);
      }
      setIsTyping(false);
      setThinkingText('');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const resetConversation = () => {
    setMessages([
      {
        id: Date.now(),
        content:
          "Hi! I'm your AI reporting assistant. Ask me questions about your store or employee metrics and I'll generate charts and analysis for you.\n\nHere are some things you can ask:\n- \"Show me actual sales for last week\"\n- \"Compare actual SPLH across all stores for the past month\"\n- \"What was our attendance rate this month?\"\n- \"Show scheduled vs actual labor hours this week as a bar chart\"",
        sender: 'ai',
        timestamp: new Date(),
        chartData: null,
      },
    ]);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [inputValue]);

  const components = {
    h1: ({ node, ...props }) => <h1 className="text-3xl font-bold mt-8" {...props} />,
    h2: ({ node, ...props }) => <h2 className="text-2xl font-bold mt-7" {...props} />,
    h3: ({ node, ...props }) => <h3 className="text-xl font-bold mt-6" {...props} />,
    h4: ({ node, ...props }) => <h4 className="text-lg font-bold mt-5" {...props} />,
    h5: ({ node, ...props }) => <h5 className="text-base font-semibold mt-4" {...props} />,
    h6: ({ node, ...props }) => <h6 className="text-sm font-semibold mt-3" {...props} />,
    p: ({ node, ...props }) => <p className="mt-3 text-base" {...props} />,
    strong: ({ node, ...props }) => <strong className="font-semibold my-3 text-sm" {...props} />,
    em: ({ node, ...props }) => <em className="italic mt-3 text-sm" {...props} />,
    ul: ({ node, ...props }) => <ul className="list-disc ml-6 mt-3 text-sm" {...props} />,
    ol: ({ node, ...props }) => <ol className="list-decimal ml-6 mt-3 text-sm" {...props} />,
    li: ({ node, ...props }) => <li className="mt-3 text-sm" {...props} />,
    blockquote: ({ node, ...props }) => (
      <blockquote className="border-l-4 border-slate-300 pl-4 italic mt-3 text-sm" {...props} />
    ),
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-white border-b px-4 py-3 flex items-center justify-between shadow-sm">
          <div className="flex items-center min-w-0">
            {props.onBack && (
              <>
                <button
                  onClick={props.onBack}
                  title={`Back to ${props.backLabel || 'Analytics'}`}
                  className="flex items-center gap-1.5 -ml-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-500 hover:text-violet-700 hover:bg-gray-100 transition-colors"
                >
                  <ArrowBackIcon style={{ fontSize: 18 }} />
                  <span>{props.backLabel || 'Analytics'}</span>
                </button>
                <div className="h-6 w-px bg-gray-200 mx-3 flex-shrink-0" />
              </>
            )}
            <div className="w-9 h-9 rounded-full bg-violet-600 flex items-center justify-center flex-shrink-0">
              <AssistantIcon fontSize="small" className="text-white" />
            </div>
            <div className="ml-3 min-w-0">
              <h2 className="font-medium text-gray-800">AI Insights</h2>
              <p className="text-xs text-gray-500 truncate">Ask questions about your metrics</p>
            </div>
          </div>
          <button
            onClick={resetConversation}
            className="text-sm text-gray-500 hover:text-violet-600"
          >
            Reset
          </button>
        </div>

        {/* Messages area */}
        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`mb-4 flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`rounded-lg px-4 max-w-3/4 ${
                  message.sender === 'user'
                    ? 'bg-violet-100 text-gray-700 py-3'
                    : 'bg-white border border-gray-200 text-gray-700 pb-3'
                }`}
                style={{ maxWidth: message.chartData ? '90%' : '75%' }}
              >
                <div className="flex flex-col">
                  {message.sender === 'ai' ? (
                    <ReactMarkdown components={components} className="mb-1">
                      {message.content}
                    </ReactMarkdown>
                  ) : (
                    <div className="mb-1">{message.content}</div>
                  )}

                  {/* Inline chart rendering */}
                  {message.chartData && message.chartData.metric_results && (
                    <div className={`mt-4 mb-2 bg-white border border-gray-200 rounded-lg p-4 ${
                      ['cards', 'table', 'pie'].includes(message.chartData.widget_type) ? 'pb-4' : 'pb-14'
                    }`}>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-semibold text-gray-700">
                          {message.chartData.title}
                        </h4>
                        <Select
                          value={message.chartData.widget_type}
                          onChange={(e) => {
                            const newType = e.target.value;
                            setMessages((prev) =>
                              prev.map((msg) =>
                                msg.id === message.id
                                  ? { ...msg, chartData: { ...msg.chartData, widget_type: newType } }
                                  : msg
                              )
                            );
                          }}
                          input={<OutlinedInput />}
                          size="small"
                          sx={{
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            height: '32px',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            '& .MuiSelect-select': {
                              paddingTop: 0,
                              paddingBottom: 0,
                              display: 'flex',
                              alignItems: 'center',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#6d28d9',
                            },
                          }}
                        >
                          {/* A by_entity chart only renders as bar or table. */}
                          {message.chartData.layout !== 'by_entity' && <MenuItem value="line">Line Chart</MenuItem>}
                          <MenuItem value="bar">Bar Chart</MenuItem>
                          {message.chartData.layout !== 'by_entity' && <MenuItem value="pie">Pie Chart</MenuItem>}
                          <MenuItem value="table">Table</MenuItem>
                          {message.chartData.layout !== 'by_entity' && <MenuItem value="cards">Metric Cards</MenuItem>}
                        </Select>
                      </div>
                      <div style={{ height: 350 }}>
                        <WidgetRenderer
                          widgetType={message.chartData.widget_type}
                          activeTab={message.chartData.metric_results.active_tab}
                          data={message.chartData.metric_results.agg_data}
                          rawData={message.chartData.metric_results.raw_data}
                          intradayData={message.chartData.metric_results.intraday_data}
                          summaryData={message.chartData.metric_results.summary_data}
                          locationData={message.chartData.metric_results.location_data}
                          selectedMetrics={message.chartData.metric_results.selected_metrics}
                          metrics={message.chartData.metric_results.metrics_info}
                          compareLocations={message.chartData.metric_results.compare_locations}
                          compareDow={message.chartData.metric_results.compare_dow}
                          layout={message.chartData.layout}
                          isDashboard={false}
                        />
                      </div>
                    </div>
                  )}

                  <span
                    className={`text-xs self-end mt-1 ${
                      message.sender === 'user' ? 'text-gray-500' : 'text-gray-400'
                    }`}
                  >
                    {formatTimestamp(message.timestamp)}
                  </span>
                </div>
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="flex items-center mb-4">
              <div className="bg-white border border-gray-200 rounded-lg px-4 py-3 max-w-3/4 flex items-center">
                <CircularProgress
                  fontSize="small"
                  className="text-violet-600 animate-spin mr-2"
                />
                <span className="text-gray-500 text-sm">
                  {thinkingText || 'Assistant is thinking...'}
                </span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="bg-white border-t p-4">
          <div className="flex">
            <div className="flex-1 bg-gray-100 rounded-full flex items-center px-4">
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Ask about your store metrics, sales, labor hours..."
                className="w-full bg-transparent py-2 outline-none resize-none text-gray-700"
                rows="1"
              />
              <button
                onClick={() => handleSendMessage()}
                disabled={!inputValue.trim() || isTyping}
                className={`ml-2 rounded-full w-8 h-8 flex items-center justify-center ${
                  inputValue.trim() && !isTyping
                    ? 'bg-violet-600 text-white'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                <SendIcon fontSize="small" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
